import React, { memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import LiveTooltip from 'live/components/ui/tooltip'
import authorization from 'live/services/authorization'

import { useBowtieLanguage } from '../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../store'
import { BOWTIE_BARRIER_SETTINGS, BOWTIE_SETTINGS_URL } from '../../../../../utils/constants'
import { ActionIcons } from './ActionIcons'
import {
    ActionsWrapperContainer,
    ActionsContainer,
    CloseBowtieContainer,
    ActionButton,
    SearchContainer
} from './styled'

interface Props {
    zoomIn?: () => void
    zoomOut?: () => void
    zoomReset?: () => void
    onCloseAll?: () => void
    onOpenAll?: () => void
    onCloseBowTie?: () => void
    rig: string
    toggleHelpModal: () => void
    toggleSearchModal: () => void
    bowtieId: number
    generateBowtiePDF: () => void
}

const ActionButtons: React.FC<Props> = props => {
    const {
        zoomIn,
        zoomOut,
        zoomReset,
        onCloseAll,
        onOpenAll,
        onCloseBowTie,
        rig,
        toggleHelpModal,
        toggleSearchModal,
        bowtieId,
        generateBowtiePDF
    } = props

    const dispatch = useDispatch()
    const { highlightDegradedBarriers } = useSelector((state: IReduxReducers) => state.highlightDegradedBarriers)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const i18n = useBowtieLanguage()

    const toggleHighlightDegraded = (): void => {
        dispatch<IReduxActions['highlightDegradedBarriers']>({
            type: 'SET_HIGHLIGHT_DEGRADED_BARRIERS',
            payload: { newHighlightDegraded: !highlightDegradedBarriers }
        })
    }

    const toggleLanguage = (): void => {
        dispatch<IReduxActions['bowtieLanguage']>({ type: 'SWITCH_LANGUAGE' })
    }

    const handleGoToEditMode = (): void => {
        window.open(`/#/config/${BOWTIE_SETTINGS_URL}?type=edit&tab=bowties&id=${bowtieId}`)
    }
    const handleGoToConfigMode = (): void => {
        window.open(
            `/#/config/${BOWTIE_BARRIER_SETTINGS}?type=list&tab=barrierStatus&page=1&perPage=50&direction=ASC&orderBy=ID`
        )
    }

    return (
        <ActionsWrapperContainer>
            <CloseBowtieContainer>
                <LiveTooltip content={i18n('Close')} position="top">
                    <ActionButton onClick={onCloseBowTie}>
                        <ActionIcons type="closeBowtie" />
                    </ActionButton>
                </LiveTooltip>
                <div className="rigTitle">{rig}</div>
            </CloseBowtieContainer>

            <SearchContainer>
                <LiveTooltip content={i18n('Search')} position="top">
                    <ActionButton onClick={toggleSearchModal}>
                        <ActionIcons type="search" />
                    </ActionButton>
                </LiveTooltip>
            </SearchContainer>

            <ActionsContainer>
                <LiveTooltip content={i18n('Toggle language')} position="top">
                    <ActionButton onClick={toggleLanguage}>
                        <ActionIcons type="language" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Help')} position="top">
                    <ActionButton onClick={toggleHelpModal}>
                        <ActionIcons type="help" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Highlight inactive and unknown barriers')} position="top">
                    <ActionButton onClick={toggleHighlightDegraded} pressed={highlightDegradedBarriers}>
                        <ActionIcons type="highlight" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Open first barriers level')} position="top">
                    <ActionButton onClick={onOpenAll}>
                        <ActionIcons type="open" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Close all barriers')} position="top">
                    <ActionButton onClick={onCloseAll}>
                        <ActionIcons type="close" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Zoom in')} position="top">
                    <ActionButton onClick={zoomIn}>
                        <ActionIcons type="zoomIn" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Zoom out')} position="top">
                    <ActionButton onClick={zoomOut}>
                        <ActionIcons type="zoomOut" />
                    </ActionButton>
                </LiveTooltip>

                <LiveTooltip content={i18n('Reset zoom')} position="top">
                    <ActionButton onClick={zoomReset}>
                        <ActionIcons type="reset" />
                    </ActionButton>
                </LiveTooltip>

                {bowtieAdmin && (
                    <LiveTooltip content={i18n('Edit this bowtie')} position="top">
                        <ActionButton onClick={handleGoToEditMode}>
                            <i className="far fa-edit"></i>
                        </ActionButton>
                    </LiveTooltip>
                )}

                {bowtieAdmin && (
                    <LiveTooltip content={i18n('Go to Settings')} position="top">
                        <ActionButton onClick={handleGoToConfigMode}>
                            <i className="far fa-cog"></i>
                        </ActionButton>
                    </LiveTooltip>
                )}

                <LiveTooltip content={i18n('Export CSV Data')} position="top">
                    <ActionButton onClick={generateBowtiePDF}>
                        <ActionIcons type="export" />
                    </ActionButton>
                </LiveTooltip>
            </ActionsContainer>
        </ActionsWrapperContainer>
    )
}

export default memo(ActionButtons)
